/**
 * OpsAuthContext — shared authentication state for the operating system.
 * Uses httpOnly cookies (set by the backend), so we don't store tokens in JS.
 * A `null` user + `loading=true` means we're still checking the session.
 */
import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/ops`;

// axios instance that always sends cookies
export const opsApi = axios.create({
  baseURL: API,
  withCredentials: true,
});

const OpsAuthContext = createContext(null);

export const OpsAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await opsApi.get('/auth/me');
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await opsApi.post('/auth/login', { email, password });
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await opsApi.post('/auth/logout');
    } catch {
      /* ignore */
    }
    setUser(null);
  }, []);

  return (
    <OpsAuthContext.Provider value={{ user, loading, login, logout, refresh }}>
      {children}
    </OpsAuthContext.Provider>
  );
};

export const useOpsAuth = () => {
  const ctx = useContext(OpsAuthContext);
  if (!ctx) throw new Error('useOpsAuth must be used inside OpsAuthProvider');
  return ctx;
};

// Human-readable role labels (Arabic)
export const ROLE_LABELS_AR = {
  executive_director: 'المدير التنفيذي',
  operations_manager: 'مسؤول التشغيل',
  track_leader: 'قائد مسار',
  member: 'عضو فريق',
};

// Role helpers — encapsulate role checks so UI never hard-codes strings.
export const isExecutiveDirector = (u) => u?.role === 'executive_director';
export const isOperationsManager = (u) => u?.role === 'operations_manager';
export const isTrackLeader = (u) => u?.role === 'track_leader';
export const isMember = (u) => u?.role === 'member';
export const canManageInbox = (u) =>
  u?.role === 'executive_director' || u?.role === 'operations_manager';

/** Convert an axios error into a plain-text message (never an object). */
export function formatAuthError(err) {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(' · ');
  if (d?.msg) return d.msg;
  return err?.message || 'حدث خطأ غير متوقع، حاولي مجددًا';
}
